import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerProductCodeCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { ProductCodeDto } from "@/features/ledger/api/dto";

/**
 * Met à jour un code produit (`PUT /ProductCodes/{id}`) — réservé à
 * flashpay-superadmin / flashpay-admin.
 */
export const useUpdateProductCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "product-codes", "update"],
    mutationFn: ({
      productCodeId,
      body,
    }: {
      productCodeId: string;
      body: ProductCodeDto;
    }) =>
      mutateData({
        url: ledgerEndpoints.productCodes.update(productCodeId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<ProductCodeDto>,
    onSuccess: (_data, { productCodeId }) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerProductCodeCacheKeys(productCodeId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateProductCode failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
